import Phaser from 'phaser';
import { GAME_WIDTH } from '../config';
import { ShopManager, ShopCategory, ShopItem, Currency } from '../ui/ShopManager';
import {
  BG_VOID_CSS,
  BG_HOVER,
  BG_ELEVATED,
  BG_PANEL,
  TEXT_PRIMARY_CSS,
  TEXT_SECONDARY_CSS,
  TEXT_TERTIARY,
  TEXT_TERTIARY_CSS,
  TEXT_DISABLED,
  ACCENT_CYAN,
  ACCENT_GOLD_CSS,
} from '../ui/theme';

// Design tokens: design/handoff/DESIGN_TOKENS.md (single source of truth).

/**
 * ShopScene: In-game shop for purchasing items with shards, coins, or real money.
 * Tabbed interface with categories: Energy, Boosters, Gacha, Shard Packs, Battle Pass, VIP.
 */
export class ShopScene extends Phaser.Scene {
  private shopManager!: ShopManager;
  private selectedCategory: ShopCategory = ShopCategory.Energy;
  private contentContainer!: Phaser.GameObjects.Container;
  private walletText!: Phaser.GameObjects.Text;
  private categoryButtons: Map<ShopCategory, { bg: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text }> = new Map();

  constructor() {
    super({ key: 'Shop' });
  }

  init(data: { shopManager?: ShopManager }): void {
    this.shopManager = data.shopManager ?? new ShopManager(100, 500);
  }

  create(): void {
    // Reset state from previous scene lifecycle (Phaser reuses scene instances)
    this.categoryButtons.clear();

    this.cameras.main.setBackgroundColor(BG_VOID_CSS);
    this.mountScanline();

    // Header
    this.add
      .text(GAME_WIDTH / 2, 40, 'SHOP', {
        font: 'bold 32px Inter',
        color: TEXT_PRIMARY_CSS,
      })
      .setOrigin(0.5)
      .setDepth(10);

    // Back button
    this.createBackButton();

    // Wallet display
    this.walletText = this.add
      .text(GAME_WIDTH - 20, 40, '', {
        font: '16px Inter',
        color: TEXT_SECONDARY_CSS,
      })
      .setOrigin(1, 0.5)
      .setDepth(10);
    this.updateWalletDisplay();

    // Category tabs
    this.createCategoryTabs();

    // Content container
    this.contentContainer = this.add.container(0, 180);
    this.contentContainer.setDepth(5);

    // Show first category
    this.showCategory(this.selectedCategory);

    this.events.on(Phaser.Scenes.Events.SHUTDOWN, this.unmountScanline, this);
    this.events.on(Phaser.Scenes.Events.DESTROY, this.unmountScanline, this);
  }

  private scanlineEl: HTMLDivElement | null = null;

  private mountScanline(): void {
    if (typeof document === 'undefined' || this.scanlineEl) return;
    const el = document.createElement('div');
    el.className = 'crt-scanline';
    document.body.appendChild(el);
    this.scanlineEl = el;
  }

  private unmountScanline(): void {
    if (this.scanlineEl) {
      this.scanlineEl.remove();
      this.scanlineEl = null;
    }
  }

  /**
   * Create the back button.
   */
  private createBackButton(): void {
    const hit = this.add.rectangle(40, 40, 60, 44, 0x000000, 0.001);
    hit.setDepth(9);
    hit.setInteractive({ useHandCursor: true });

    const backBtn = this.add.rectangle(40, 40, 60, 36, BG_HOVER, 0.3);
    backBtn.setStrokeStyle(1, TEXT_TERTIARY, 0.5);
    backBtn.setDepth(10);

    this.add
      .text(40, 40, '< Back', {
        font: '14px Inter',
        color: TEXT_SECONDARY_CSS,
      })
      .setOrigin(0.5)
      .setDepth(11);

    hit.on('pointerup', () => {
      this.scene.start('Menu');
    });
  }

  /**
   * Update the wallet currency display.
   */
  private updateWalletDisplay(): void {
    const shards = this.shopManager.getShards();
    const coins = this.shopManager.getCoins();
    this.walletText.setText(`${shards} Shards | ${coins} Coins`);
  }

  /**
   * Tabs use neutral bg-elevated backgrounds; the selected tab gets a
   * bottom border in accent-primary. Family-color fills are forbidden here
   * (Anti-Pattern #2 in the handoff: max 2 accent colours per screen).
   */
  private createCategoryTabs(): void {
    const categories: { cat: ShopCategory; label: string }[] = [
      { cat: ShopCategory.Energy, label: 'Energy' },
      { cat: ShopCategory.Boosters, label: 'Boost' },
      { cat: ShopCategory.Gacha, label: 'Gacha' },
      { cat: ShopCategory.ShardPacks, label: 'Shards' },
      { cat: ShopCategory.BattlePass, label: 'Pass' },
      { cat: ShopCategory.VIP, label: 'VIP' },
    ];

    const tabWidth = GAME_WIDTH / categories.length;
    const tabY = 100;

    for (let i = 0; i < categories.length; i++) {
      const { cat, label } = categories[i];
      const x = tabWidth * i + tabWidth / 2;
      const isSelected = cat === this.selectedCategory;

      const bg = this.add.rectangle(x, tabY, tabWidth - 4, 44, BG_ELEVATED, 0.6);
      bg.setStrokeStyle(isSelected ? 2 : 0, ACCENT_CYAN, 0.8);
      bg.setDepth(10);
      bg.setInteractive({ useHandCursor: true });

      const text = this.add
        .text(x, tabY, label, {
          font: 'bold 12px Inter',
          color: isSelected ? TEXT_PRIMARY_CSS : TEXT_TERTIARY_CSS,
        })
        .setOrigin(0.5)
        .setDepth(11);

      this.categoryButtons.set(cat, { bg, text });

      bg.on('pointerup', () => {
        this.selectedCategory = cat;
        this.updateCategoryHighlight();
        this.showCategory(cat);
      });
    }
  }

  /** Highlight the active category tab. */
  private updateCategoryHighlight(): void {
    for (const [cat, { bg, text }] of this.categoryButtons) {
      if (cat === this.selectedCategory) {
        bg.setAlpha(1);
        bg.setStrokeStyle(2, ACCENT_CYAN, 0.8);
        text.setColor(TEXT_PRIMARY_CSS);
      } else {
        bg.setAlpha(0.6);
        bg.setStrokeStyle(0, ACCENT_CYAN, 0);
        text.setColor(TEXT_TERTIARY_CSS);
      }
    }
  }

  /**
   * Display items for a given category.
   */
  private showCategory(category: ShopCategory): void {
    this.contentContainer.removeAll(true);

    const items = this.shopManager.getItemsByCategory(category);
    const cardWidth = GAME_WIDTH - 40;
    const cardHeight = 100;
    const padding = 20;
    const startY = 10;

    if (items.length === 0) {
      const emptyText = this.add.text(GAME_WIDTH / 2, 100, 'No items available', {
        font: '16px Inter',
        color: TEXT_TERTIARY_CSS,
      }).setOrigin(0.5);
      this.contentContainer.add(emptyText);
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const y = startY + i * (cardHeight + 12);
      this.createItemCard(item, padding, y, cardWidth, cardHeight);
    }
  }

  /**
   * Create a shop item card with buy button.
   */
  private createItemCard(
    item: ShopItem,
    x: number,
    y: number,
    width: number,
    height: number
  ): void {
    const canAfford = this.shopManager.canAfford(item.id);

    const bg = this.add.rectangle(
      x + width / 2,
      y + height / 2,
      width,
      height,
      BG_PANEL,
      canAfford ? 1 : 0.7
    );
    bg.setStrokeStyle(1, BG_HOVER, canAfford ? 0.6 : 0.3);
    this.contentContainer.add(bg);

    const nameColor = canAfford ? TEXT_PRIMARY_CSS : TEXT_TERTIARY_CSS;
    const nameText = this.add.text(x + 15, y + 12, item.name, {
      font: 'bold 18px Inter',
      color: nameColor,
    });
    this.contentContainer.add(nameText);

    const descText = this.add.text(x + 15, y + 36, item.description, {
      font: '13px Inter',
      color: TEXT_TERTIARY_CSS,
      wordWrap: { width: width - 130 },
    });
    this.contentContainer.add(descText);

    const priceText = this.getPriceText(item);
    const priceDisplay = this.add.text(x + 15, y + height - 24, priceText, {
      font: '14px Inter',
      color: canAfford ? ACCENT_GOLD_CSS : TEXT_TERTIARY_CSS,
    });
    this.contentContainer.add(priceDisplay);

    if (item.purchaseLimit > 0) {
      const count = this.shopManager.getPurchaseCount(item.id);
      const limitText = this.add.text(
        x + width - 90,
        y + 12,
        `${count}/${item.purchaseLimit}`,
        {
          font: '12px Inter',
          color: TEXT_SECONDARY_CSS,
        }
      );
      this.contentContainer.add(limitText);
    }

    // Buy CTA — paired-glow rectangle beneath for accent halo.
    const btnX = x + width - 50;
    const btnY = y + height - 28;
    const btnColor = canAfford ? ACCENT_CYAN : TEXT_DISABLED;

    if (canAfford) {
      const glow = this.add.rectangle(btnX, btnY, 96, 42, ACCENT_CYAN, 0.15);
      this.contentContainer.add(glow);
    }

    const buyBtn = this.add.rectangle(btnX, btnY, 80, 44, btnColor, canAfford ? 0.3 : 0.1);
    buyBtn.setStrokeStyle(1, btnColor, canAfford ? 0.6 : 0.2);
    this.contentContainer.add(buyBtn);

    const buyText = this.add
      .text(btnX, btnY, canAfford ? 'BUY' : 'SOLD', {
        font: 'bold 14px Inter',
        color: canAfford ? TEXT_PRIMARY_CSS : TEXT_TERTIARY_CSS,
      })
      .setOrigin(0.5);
    this.contentContainer.add(buyText);

    if (canAfford) {
      buyBtn.setInteractive({ useHandCursor: true });

      buyBtn.on('pointerover', () => {
        buyBtn.setFillStyle(btnColor, 0.5);
      });

      buyBtn.on('pointerout', () => {
        buyBtn.setFillStyle(btnColor, 0.3);
      });

      buyBtn.on('pointerup', () => {
        const success = this.shopManager.purchase(item.id);
        if (success) {
          this.updateWalletDisplay();
          this.showCategory(this.selectedCategory); // Refresh
        }
      });
    }
  }

  /**
   * Format the price text for an item.
   */
  private getPriceText(item: ShopItem): string {
    if (item.realMoneyPriceCents !== null && item.price === 0) {
      const dollars = (item.realMoneyPriceCents / 100).toFixed(2);
      return `$${dollars}`;
    }

    const currencyLabel = item.currency === Currency.Shards ? 'Shards' : 'Coins';
    return `${item.price} ${currencyLabel}`;
  }
}
